/** Sprint 4's closed, value-free wire contract. Shared by Pages, Reporting and the browser.
 * Frozen recipe/patch validators remain unchanged. No financial arithmetic belongs here.
 */
import {
  PANEL_REGISTRY,
  PANEL_REGISTRY_V3,
  CANONICAL_TRAJECTORY_MODES,
  stableJson,
  sha256Hex,
} from "./historical-dashboard-contract.js";
import {
  HISTORICAL_ENTITY_CODES,
  HISTORICAL_PERIODS,
  canonicalizeHistoricalDashboardRecipeV3,
  canonicalizeHistoricalDashboardPatch,
  canonicalizeHistoricalDashboardPatchV3,
} from "./historical-dashboard-patch.js";
export const RESPONSE_PROTOCOL = "historical-response/v1";
export const INTENT_SCHEMA = "historical-response-intent/v1";
export const PRESENTATION_SCHEMA = "historical-presentation/v1";
export const QUESTION_FIELDS = Object.freeze({
  latest_revenue: "latestRevenueUsdM",
  entry_multiple: "multiple",
  entry_cagr: "cagr",
  entry_dollar_increase: "contributionUsdM",
});
export const RESPONSE_FAILURES = Object.freeze([
  "invalid_intent",
  "empty_model_output",
  "missing_tool_call",
  "model_refusal",
  "model_error",
  "output_limit",
  "cancelled",
  "preparation_failed",
  "reporting_failed",
  "persistence_failed",
  "managed_scope_unavailable",
  "revision_conflict",
]);
export const ALTERNATIVES = Object.freeze([
  "compare_latest",
  "canonical_dashboard",
  "reporting_coverage",
  "manual_exploration",
  "capabilities",
]);
export const CLARIFICATION_OPTIONS = Object.freeze({
  growth_measure: ["entry_multiple", "entry_cagr", "entry_dollar_increase"],
  company_scope: ["core_seven", "choose_companies"],
  period_scope: [...HISTORICAL_PERIODS],
});
const PANELS = PANEL_REGISTRY_V3.map((p) => p.panelKey);
const CONTEXT_KEYS = [
  "output",
  "question",
  "operation",
  "entityCodes",
  "period",
  "displayMode",
  "panelKeys",
];
const UUID =
  /^[0-9a-f]{8}-[0-9a-f]{4}-[1-8][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i;
const REF = /^har_[0-9a-f]{64}$/;
export const exactResponseKeys = (v, keys) =>
  v !== null &&
  typeof v === "object" &&
  !Array.isArray(v) &&
  Object.keys(v).length === keys.length &&
  keys.every((k) => Object.hasOwn(v, k));
const list = (v, allowed, min = 1, max = allowed.length) =>
  Array.isArray(v) &&
  v.length >= min &&
  v.length <= max &&
  new Set(v).size === v.length &&
  v.every((x) => allowed.includes(x));
function demand(ok, code = "invalid_intent") {
  if (!ok) throw Object.assign(new Error(code), { code });
}
function validContext(v, reason = null) {
  if (
    !exactResponseKeys(v, CONTEXT_KEYS) ||
    !["answer", "chart", "dashboard", "table"].includes(v.output) ||
    !CANONICAL_TRAJECTORY_MODES.includes(v.displayMode) ||
    !list(
      v.entityCodes,
      HISTORICAL_ENTITY_CODES,
      reason === "company_scope" ? 0 : 1,
    ) ||
    !(reason === "period_scope"
      ? v.period === null
      : HISTORICAL_PERIODS.includes(v.period))
  )
    return false;
  if (reason === "growth_measure")
    return (
      v.question === null &&
      ["answer", "table"].includes(v.output) &&
      (v.output === "answer"
        ? ["values", "leader"].includes(v.operation)
        : v.operation === "values") &&
      ["full_history", "since_entry"].includes(v.period) &&
      Array.isArray(v.panelKeys) &&
      v.panelKeys.length === 0
    );
  if (!list(v.panelKeys, PANELS)) return false;
  if (v.output === "dashboard")
    return v.question === "canonical_dashboard" && v.operation === "none";
  if (v.output === "table" && v.question === "selected_data")
    return v.operation === "values" && v.panelKeys.length === 1;
  if (
    !Object.hasOwn(QUESTION_FIELDS, v.question) ||
    v.panelKeys.length !== 1 ||
    v.panelKeys[0] !== "company_diagnostics"
  )
    return false;
  if (
    v.question !== "latest_revenue" &&
    reason !== "period_scope" &&
    !["full_history", "since_entry"].includes(v.period)
  )
    return false;
  if (v.output === "answer") return ["values", "leader"].includes(v.operation);
  return (
    v.operation === "values" &&
    (v.output === "table" ||
      (v.output === "chart" && v.question === "latest_revenue"))
  );
}
export function validateResponseIntent(v) {
  demand(v?.schemaVersion === INTENT_SCHEMA);
  if (v.kind === "analysis")
    demand(
      exactResponseKeys(v, ["schemaVersion", "kind", ...CONTEXT_KEYS]) &&
        validContext(Object.fromEntries(CONTEXT_KEYS.map((k) => [k, v[k]]))),
    );
  else if (v.kind === "help")
    demand(
      exactResponseKeys(v, ["schemaVersion", "kind", "topic"]) &&
        ["capabilities", "revenue_basis", "comparison_caveats"].includes(
          v.topic,
        ),
    );
  else if (v.kind === "action")
    demand(
      exactResponseKeys(v, ["schemaVersion", "kind", "action"]) &&
        v.action === "export_current_table",
    );
  else if (v.kind === "unsupported")
    demand(
      exactResponseKeys(v, [
        "schemaVersion",
        "kind",
        "reason",
        "alternatives",
      ]) &&
        [
          "metric",
          "entity",
          "period",
          "forecast",
          "output",
          "unsafe_request",
        ].includes(v.reason) &&
        list(v.alternatives, ALTERNATIVES, 0, 3),
    );
  else if (v.kind === "clarification")
    demand(
      exactResponseKeys(v, [
        "schemaVersion",
        "kind",
        "reason",
        "options",
        "context",
      ]) &&
        Object.hasOwn(CLARIFICATION_OPTIONS, v.reason) &&
        list(v.options, CLARIFICATION_OPTIONS[v.reason], 2, 4) &&
        validContext(v.context, v.reason),
    );
  else demand(false);
  return v;
}
export function validatePresentation(v) {
  const fail = () => demand(false, "descriptor_invalid");
  if (
    !exactResponseKeys(v, [
      "schemaVersion",
      "mode",
      "question",
      "operation",
      "panelKey",
      "sort",
    ]) ||
    v.schemaVersion !== PRESENTATION_SCHEMA
  )
    return fail();
  if (v.mode === "dashboard") {
    if (
      v.question !== null ||
      v.operation !== "none" ||
      v.panelKey !== null ||
      v.sort !== null
    )
      return fail();
    return v;
  }
  if (v.mode === "table" && v.question === "selected_data") {
    if (
      v.operation !== "values" ||
      !PANELS.includes(v.panelKey) ||
      v.sort !== "entity"
    )
      return fail();
    return v;
  }
  if (
    !Object.hasOwn(QUESTION_FIELDS, v.question) ||
    v.panelKey !== "company_diagnostics"
  )
    return fail();
  if (v.mode === "answer") {
    if (
      !(
        (v.operation === "values" && v.sort === "entity") ||
        (v.operation === "leader" && v.sort === "measure_desc")
      )
    )
      return fail();
  } else if (v.mode === "chart") {
    if (
      v.question !== "latest_revenue" ||
      v.operation !== "values" ||
      v.sort !== "latest_revenue_desc"
    )
      return fail();
  } else if (v.mode === "table") {
    if (
      v.operation !== "values" ||
      ![
        "entity",
        "measure_desc",
        ...(v.question === "latest_revenue" ? ["latest_revenue_desc"] : []),
      ].includes(v.sort)
    )
      return fail();
  } else return fail();
  return v;
}
export const dashboardPresentation = () => ({
  schemaVersion: PRESENTATION_SCHEMA,
  mode: "dashboard",
  question: null,
  operation: "none",
  panelKey: null,
  sort: null,
});
export function presentationFromIntent(input) {
  const v = validateResponseIntent(input);
  demand(v.kind === "analysis");
  return validatePresentation(
    v.output === "dashboard"
      ? dashboardPresentation()
      : {
          schemaVersion: PRESENTATION_SCHEMA,
          mode: v.output,
          question: v.question,
          operation: v.operation,
          panelKey: v.panelKeys[0],
          sort:
            v.output === "chart"
              ? "latest_revenue_desc"
              : v.operation === "leader"
                ? "measure_desc"
                : "entity",
        },
  );
}
export function recipeFromIntent(input) {
  const v = validateResponseIntent(input);
  demand(v.kind === "analysis");
  return canonicalizeHistoricalDashboardRecipeV3({
    schemaVersion: "historical-dashboard-recipe/v3",
    datasetKey: "techfund_operating_history",
    cohortKey: "greenpoint_core_seven",
    metricKey: "revenue",
    basis: "actual_point_in_time",
    panels: v.panelKeys.map((key) => {
      const { panelKey, queryKey, componentKey, tableViewKey } =
        PANEL_REGISTRY_V3.find((p) => p.panelKey === key);
      return { panelKey, queryKey, componentKey, tableViewKey };
    }),
    supportingTable: "follow_selected_panel",
    trace: "available_collapsed",
    entityCodes: v.entityCodes,
    period: v.period,
    viewPreferences: { trajectoryMode: v.displayMode },
  });
}
export function assertPresentationRecipe(p, recipe) {
  validatePresentation(p);
  const r = canonicalizeHistoricalDashboardRecipeV3({
    ...recipe,
    schemaVersion: "historical-dashboard-recipe/v3",
  });
  demand(
    p.mode === "dashboard" || r.panels.some((x) => x.panelKey === p.panelKey),
    "descriptor_incompatible",
  );
  demand(
    !p.question?.startsWith("entry_") ||
      ["full_history", "since_entry"].includes(r.period),
    "descriptor_incompatible",
  );
  return p;
}
export function completeClarification(input, option, companies) {
  const v = validateResponseIntent(input);
  demand(v.kind === "clarification" && v.options.includes(option));
  const context = structuredClone(v.context);
  if (v.reason === "growth_measure") {
    context.question = option;
    context.panelKeys = ["company_diagnostics"];
  }
  if (v.reason === "period_scope") context.period = option;
  if (v.reason === "company_scope")
    context.entityCodes =
      option === "core_seven" ? [...HISTORICAL_ENTITY_CODES] : companies;
  return validateResponseIntent({
    schemaVersion: INTENT_SCHEMA,
    kind: "analysis",
    ...context,
  });
}
export function taskIntent(task) {
  const base = {
    schemaVersion: INTENT_SCHEMA,
    kind: "analysis",
    output: "dashboard",
    question: "canonical_dashboard",
    operation: "none",
    entityCodes: [...HISTORICAL_ENTITY_CODES],
    period: "full_history",
    displayMode: "entry_indexed_log",
    panelKeys: PANEL_REGISTRY.map((p) => p.panelKey),
  };
  if (task === "compare_latest")
    Object.assign(base, {
      output: "chart",
      question: "latest_revenue",
      operation: "values",
      displayMode: "annualized_revenue_calendar",
      panelKeys: ["company_diagnostics"],
    });
  else if (task === "reporting_coverage")
    base.panelKeys = ["reporting_coverage_heatmap"];
  else demand(task === "canonical_dashboard");
  return base;
}
/** No-result receipts bind an export to its original owned snapshot, never the UI's current selection. */
export function validateResponseReceipt(v) {
  demand(
    exactResponseKeys(v, ["schemaVersion", "intent", "context", "failure"]) &&
      v.schemaVersion === RESPONSE_PROTOCOL,
    "response_invalid",
  );
  demand(
    v.failure === null || RESPONSE_FAILURES.includes(v.failure),
    "response_invalid",
  );
  if (v.failure !== null) demand(v.intent === null, "response_invalid");
  else {
    validateResponseIntent(v.intent);
    demand(v.intent.kind !== "analysis", "response_invalid");
  }
  if (v.context !== null) {
    demand(
      exactResponseKeys(v.context, [
        "conversationId",
        "baseRevisionId",
        "panelKey",
        "resultRef",
      ]) &&
        UUID.test(v.context.conversationId) &&
        UUID.test(v.context.baseRevisionId) &&
        PANELS.includes(v.context.panelKey) &&
        REF.test(v.context.resultRef),
      "response_invalid",
    );
  }
  demand(v.intent?.kind !== "action" || v.context !== null, "response_invalid");
  return v;
}
export const responseDigest = (v) => sha256Hex(stableJson(v));
export const responseReceiptText = (v) => {
  validateResponseReceipt(v);
  if (v.failure)
    return (
      {
        cancelled: "Cancelled. No new analysis saved.",
        managed_scope_unavailable:
          "The approved revenue method cannot compare this complete scope. Choose a broader period or different companies; no partial comparison was saved.",
        revision_conflict:
          "This analysis has a newer revision. Reload it before making another change.",
      }[v.failure] ||
      "This request could not be completed. No new analysis was saved."
    );
  if (v.intent.kind === "help")
    return {
      capabilities:
        "Compare reported revenue for the core seven as an answer, chart, table or dashboard. Entry comparisons support revenue multiple, CAGR and dollar increase. Explore data manually for other datasets.",
      revenue_basis:
        "Indexed revenue expresses change from the comparison starting observation. Annualized revenue uses the managed source basis and currency conversion. Reporting dates and revenue definitions can differ between companies.",
      comparison_caveats:
        "Compare source dates and revenue bases before interpreting differences. Missing observations are not zero and reported dates need not be synchronized. The approved method requires complete eligible data for the selected scope.",
    }[v.intent.topic];
  if (v.intent.kind === "clarification")
    return {
      growth_measure: "How should revenue growth be compared?",
      company_scope: "Which supported companies should this analysis include?",
      period_scope:
        "Which supported reporting period should this analysis use?",
    }[v.intent.reason];
  if (v.intent.kind === "action")
    return "The supporting data for this saved view is ready to export.";
  return {
    metric:
      "This analysis supports reported revenue. That metric is outside the approved methods.",
    entity:
      "That company is outside the supported core seven. Choose supported companies.",
    period: "That period is outside the supported analysis windows.",
    forecast:
      "This workspace analyzes reported historical revenue. Forecasting is not supported.",
    output:
      "That output is unavailable here. Create or open an analysis first.",
    unsafe_request:
      "This workspace cannot execute SQL, code or instructions to access other records.",
  }[v.intent.reason];
};

export function validateResponseRequest(v) {
  demand(
    exactResponseKeys(v, [
      "schemaVersion",
      "requestId",
      "conversationId",
      "baseRevisionId",
      "panelKey",
      "action",
      "input",
    ]) &&
      v.schemaVersion === "historical-response-request/v1" &&
      UUID.test(v.requestId),
    "request_invalid",
  );
  demand(
    (v.conversationId === null || UUID.test(v.conversationId)) &&
      (v.baseRevisionId === null || UUID.test(v.baseRevisionId)) &&
      (v.panelKey === null || PANELS.includes(v.panelKey)),
    "request_invalid",
  );
  demand(
    v.conversationId !== null ||
      (v.baseRevisionId === null && v.panelKey === null),
    "request_invalid",
  );
  demand(v.baseRevisionId !== null || v.panelKey === null, "request_invalid");
  if (v.action === "submit") {
    if (exactResponseKeys(v.input, ["prompt"]))
      demand(
        typeof v.input.prompt === "string" &&
          v.input.prompt.trim().length > 0 &&
          [...v.input.prompt].length <= 4000,
        "request_invalid",
      );
    else {
      demand(exactResponseKeys(v.input, ["intent"]), "request_invalid");
      validateResponseIntent(v.input.intent);
    }
  } else if (v.action === "edit") {
    demand(
      v.baseRevisionId !== null &&
        exactResponseKeys(v.input, ["patch", "presentation"]),
      "request_invalid",
    );
    validatePresentation(v.input.presentation);
    demand(
      v.input.patch === null ||
        (typeof v.input.patch === "object" &&
          [
            "historical-dashboard-patch/v2",
            "historical-dashboard-patch/v3",
          ].includes(v.input.patch.schemaVersion)),
      "request_invalid",
    );
    if (v.input.patch !== null) {
      if (v.input.patch.schemaVersion === "historical-dashboard-patch/v3")
        canonicalizeHistoricalDashboardPatchV3(v.input.patch);
      else canonicalizeHistoricalDashboardPatch(v.input.patch);
    }
  } else
    demand(
      ["undo", "duplicate"].includes(v.action) &&
        v.baseRevisionId !== null &&
        v.input === null,
      "request_invalid",
    );
  return v;
}
export const responseRequestHash = (owner, body) =>
  responseDigest({
    owner: owner.trim().toLowerCase(),
    route: "/v1/gp/portfolio/techfund/analysis/response",
    protocol: RESPONSE_PROTOCOL,
    body: validateResponseRequest(body),
  });
